import {
  useCancelInvitation,
  useInvitations,
  useInviteMember,
  useAccess,
  useResendInvitation,
  useRoles,
} from '@church/data'
import {
  faBan,
  faCheck,
  faClock,
  faEnvelopeOpenText,
  faPaperPlane,
  faUserPlus,
} from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { InviteForm } from '../components/access/InvitePanels.tsx'
import {
  ListCard,
  ListRow,
  ListSection,
  NewButton,
  usePanel,
} from '../components/platform/SettingsList.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Icon,
  PanelSection,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'

/**
 * Invitations waiting for an answer. Inviting someone and cancelling an
 * invitation happen in a panel; the list only says who, as what, until when.
 */
export const Route = createFileRoute('/_authed/_shell/settings/invitations')({
  component: InvitationsTab,
})

const day = (iso: string | Date) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })

function InvitationsTab() {
  const { data: invitations, isPending } = useInvitations()
  const { data: roles } = useRoles()
  const cancel = useCancelInvitation()
  const resend = useResendInvitation()
  const invite = useInviteMember()
  const inviteFormId = useId()
  // Which invitation was just sent again, for the button's "Sent" state.
  const [resent, setResent] = useState<string | null>(null)
  const access = useAccess()
  const panel = usePanel<string>()
  const invitation = invitations?.find((i) => i.id === panel.shown)
  const roleLabel = (id: string | null) => roles?.find((r) => r.id === id)?.label ?? id ?? 'Member'

  return (
    <div className="max-w-2xl">
      <ListSection
        title="Invitations"
        icon={faEnvelopeOpenText}
        action={
          access.managesMembership && (
            <NewButton label="Invite someone" icon={faUserPlus} onClick={() => panel.show('new')} />
          )
        }
      >
        {isPending ? (
          <Spinner />
        ) : !invitations || invitations.length === 0 ? (
          <Card>
            <EmptyState
              title="No pending invitations"
              hint="Invite staff and volunteers by email; they choose their own password."
            />
          </Card>
        ) : (
          <ListCard>
            {invitations.map((i) => (
              <ListRow
                key={i.id}
                title={i.email}
                meta={
                  <span className="inline-flex items-center gap-1.5">
                    <Icon icon={faClock} className="size-3" />
                    Expires {day(i.expiresAt)}
                  </span>
                }
                aside={<Chip>{roleLabel(i.role)}</Chip>}
                onOpen={access.managesMembership ? () => panel.show(i.id) : undefined}
              />
            ))}
          </ListCard>
        )}
      </ListSection>

      <SidePanel
        open={panel.open && (panel.shown === 'new' || invitation !== undefined)}
        onOpenChange={(next) => {
          if (!next) invite.reset()
          panel.onOpenChange(next)
        }}
        title={panel.shown === 'new' ? 'Invite someone' : (invitation?.email ?? '')}
        description={panel.shown === 'new' ? undefined : 'Waiting for an answer.'}
        primary={
          panel.shown === 'new' && (
            <Button
              type="submit"
              form={inviteFormId}
              icon={faPaperPlane}
              className="py-1.5"
              pending={invite.isPending}
              pendingLabel="Sending…"
            >
              Send
            </Button>
          )
        }
        footer={
          panel.shown !== 'new' && invitation ? (
            <ConfirmButton
              label="Cancel invitation"
              icon={faBan}
              variant="quietDanger"
              className="-ml-4"
              disabled={cancel.isPending}
              title={`Cancel the invitation to ${invitation.email}?`}
              description="The link in their email stops working."
              confirmLabel="Cancel invitation"
              onConfirm={() => cancel.mutate(invitation.id, { onSuccess: panel.close })}
            />
          ) : undefined
        }
      >
        {panel.shown === 'new' ? (
          <InviteForm formId={inviteFormId} invite={invite} onDone={panel.close} />
        ) : (
          invitation && (
            <>
              <PanelSection title="Role">
                <p className="text-base font-medium text-content-default">
                  {roleLabel(invitation.role)}
                </p>
              </PanelSection>
              <PanelSection title="Expires">
                <p className="text-base font-medium text-content-default">
                  {new Date(invitation.expiresAt).toLocaleDateString(undefined, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                </p>
              </PanelSection>
              <PanelSection
                title="Email"
                hint="Sends the same link again, good for another seven days from now."
              >
                <Button
                  variant="secondary"
                  icon={resent === invitation.id ? faCheck : faPaperPlane}
                  disabled={resend.isPending || resent === invitation.id}
                  onClick={() =>
                    resend.mutate(
                      { email: invitation.email, role: invitation.role ?? 'member' },
                      { onSuccess: () => setResent(invitation.id) },
                    )
                  }
                >
                  {resent === invitation.id ? 'Sent again' : 'Resend'}
                </Button>
              </PanelSection>
              <ErrorNote error={cancel.error ?? resend.error} />
            </>
          )
        )}
      </SidePanel>
    </div>
  )
}
